import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { debounce } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { TKey, usePublicaTranslation } from '~/src/dash/publicaUI';

interface IProps {
  fetchOptions: (query: string) => Promise<{ label: string }[]>;
  labelTKey: TKey;
  placeholderTKey?: TKey;
  selectedIDs: string[];
  onChange?: (selectedValues: { label: string }[]) => void;
}

export const AutoCompleteWithChips = ({
  selectedIDs,
  fetchOptions,
  onChange,
  labelTKey,
  placeholderTKey,
}: IProps): React.ReactElement => {
  const { t } = usePublicaTranslation();

  // Set default values after translation hook is available
  const [options, setOptions] = useState<{ label: string }[]>([]);

  // Initialize with selected values and track internal state
  const [currentValue, setCurrentValue] = useState<{ label: string }[]>(() => {
    return selectedIDs.map((id) => ({ label: id }));
  });

  // Update internal state when selectedIDs prop changes (when loading saved data)
  useEffect(() => {
    const newSelectedOptions = selectedIDs.map((id) => ({ label: id }));
    setCurrentValue(newSelectedOptions);
  }, [selectedIDs.join(',')]); // Only update when the actual IDs change

  const handleSearch = async (query: string): Promise<void> => {
    try {
      const results = await fetchOptions(query);
      setOptions(results);
    } catch (error) {
      console.error('Error fetching search results:', error);
      setOptions([]);
    }
  };

  // Combine search results with current selected values to ensure they're always available
  const availableOptions = useMemo(() => {
    const optionMap = new Map();

    // Add current selections first
    currentValue.forEach((option) => {
      if (!!option.label) {
        optionMap.set(option.label, option);
      }
    });

    // Add search results
    options.forEach((option) => {
      if (!!option.label) {
        optionMap.set(option.label, option);
      }
    });

    return Array.from(optionMap.values()) as { label: string }[];
  }, [options, currentValue]);

  // Debounce backend calls to avoid excessive API requests
  const debouncedSearch = useMemo(() => {
    return debounce(handleSearch, 300);
  }, [fetchOptions]);

  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string): void => {
    void debouncedSearch(newInputValue);
  };

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleChange = (_event: React.SyntheticEvent, newValue: { label: string }[]): void => {
    setCurrentValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <Autocomplete
      multiple
      id='tags-standard'
      options={availableOptions}
      value={currentValue}
      getOptionLabel={(option) => option.label}
      filterOptions={(options) => options}
      onChange={handleChange}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          variant='standard'
          label={t(labelTKey)}
          placeholder={t(placeholderTKey || 'Search')}
        />
      )}
    />
  );
};
